var {AmpersandState} = require('catalog/libs');

/**
 * Dynamic Tab Container
 * to support dynamic child panes, instantiate with options parameter that includes availableComponents
 * map of required components
 */
var HPELog = require('core/log');
var HPEServices = require('../../base/hpe.services');
var HPEConstants = require('../../base/hpe.constants');
var PageRouter = require('../../base/hpe.page.router');
var DynamicTabContainerView = require('./hpe.dynamic.tab.container.view');
var DynamicTabContainerState = require('./hpe.dynamic.tab.container.model');


module.exports = AmpersandState.extend({

    props: {
        availableComponents: 'object',
        element: 'object',
        tabElementList: 'array',
        view: 'object',
        model: 'object',
        router: 'object',
        isInited: ['boolean', false, false]
    },

    /**
     * initialization
     */
    initialize: function(options) {

        var that = this;
        var initialState;

        //options initialization
        options = options || {};
        this.element = options.element || document.querySelector('.dynamic-tab-container');

        //do not initialize unless DOM element defined
        if (this.element) {

            //get initial state from DOM
            initialState = this.getInitialState();

            //helper/utilities initialization
            this.router = new PageRouter();

            //create base model + view
            this.model = new DynamicTabContainerState(initialState);
            this.view = new DynamicTabContainerView({
                el: that.element,
                model: that.model
            });

            //event listeners
            HPEServices.subscribe(HPEConstants.router.BROWSER_PAGE_CHANGE, this, this.onBrowserPageChange);
            HPEServices.subscribe(HPEConstants.dynamictabcontainer.CHILDINIT, this, this.onDynamicTabContainerChildInit);

            HPEServices.publish(HPEConstants.dynamictabcontainer.TAB_CONTAINER_INIT);
        }
    },

    /**
     * get the initial state of the tab container from DOM values
     * @return {object} initial state object with key/value pairs to populate model
     */
    getInitialState: function() {

        var initialState = {};

        //save a DOM reference to the list of tabs
        this.tabElementList = [].slice.call(this.element.querySelectorAll('[data-hook=dynamic-tab-container-tabs] a'));

        //pool stores instances of instantiated child components so we can call destoy and render methods on them
        initialState.pool = {};

        //generate list of valid class constant values based on tab values
        initialState.validTabConstants = this.tabElementList.map(function(tab) {
            var constantID = tab.getAttribute('data-instance-class');
            return (HPEConstants.componentclass[constantID]) ? HPEConstants.componentclass[constantID] : false;
        });

        //generate list of optional service urls that may be paired with tabs/tab content components
        initialState.serviceURLs = this.tabElementList.reduce(function(pathMap, tab) {
            var url = tab.getAttribute('data-service-path');
            var constantID = tab.getAttribute('data-instance-class');
            if (url && constantID) {
                pathMap[constantID] = url;
            }
            return pathMap;
        }, {});

        return initialState;
    },

    /**
     * look in DOM to determine the controller class constant of the active component as initial state
     * @return {string} constant string of active component (or undefined if unknown)
     */
    getDefaultChildClassConstantFromDOM: function() {

        var currentTabClassConstant, classConstant;

        //get the shortened class constant for the default active tab/pane
        currentTabClassConstant = this.tabElementList.filter(function(tab) {
            return tab.classList.contains('active');
        });
        currentTabClassConstant = (currentTabClassConstant.length) ? currentTabClassConstant[0].getAttribute('data-instance-class') : '';

        //get the full class constant for the default active tab/pane
        classConstant = HPEConstants.componentclass[currentTabClassConstant];

        //component must be known to be valid
        return (this.availableComponents[classConstant]) ? classConstant : undefined;
    },

    /**
     * update tab state/view with desired tab
     * @param {string} classConstantID ID of the class constent to make the currently active tab
     */
    updateTabState: function(classConstantID) {

        var contentEl, initParams;
        var classConstant = HPEConstants.componentclass[classConstantID];

        //remove existing view
        if (this.model.currentDynamicTabConstant) {
            if (!this.model.pool[this.model.currentDynamicTabConstant].shouldDestroy) {
                this.model.pool[this.model.currentDynamicTabConstant].destroyView();
            } else {
                this.model.pool[this.model.currentDynamicTabConstant].destroy();
                this.model.pool[this.model.currentDynamicTabConstant] = null;
                delete this.model.pool[this.model.currentDynamicTabConstant];
            }
        }

        //update tab & panel UI state to match selection
        this.view.update(classConstantID);

        //get the panel content target element to contain the content
        contentEl = document.querySelector('[data-hook=dynamic-tab-container-panel-content]');

        //add requested view:
        //if already instatiated, just call render
        if (this.model.pool[classConstant]) {
            this.model.pool[classConstant].render(contentEl);

        //not yet instantiated, do so for the first time with params, save a reference in pool
        } else {

            //initialize parameters for initialization
            initParams = {
                el: contentEl
            };

            //component may optionally have an associate service path
            if (this.model.serviceURLs[classConstantID]) {
                initParams.servicePath = this.model.serviceURLs[classConstantID];
            }

            //create new component instance an do one-time initialization
            this.model.pool[classConstant] = new this.availableComponents[classConstant]();
            this.model.pool[classConstant].init(initParams);
        }

        //update model with currently active tab/panel component constant
        this.model.currentDynamicTabConstant = classConstant;
    },

    /**
     * handle browser router page change event
     * this event triggers tab/panel view changes
     * @param {object} pagestate page router change object
     */
    onBrowserPageChange: function(pagestate) {

        var classConstant, classConstantID;

        //class constant ID is passed as part of state parameter from router event
        classConstantID = (pagestate && pagestate.state && pagestate.state.id) ? pagestate.state.id : '';

        //empty constant ID may be received if browser popstate is fired without state value
        //in this case, default to first item in known valid tab constants list
        // if ((classConstantID === '') && (this.model.validTabConstants)) {
        //     classConstantID = this.model.validTabConstants[0].split('.').pop().toUpperCase();
        // }
        //
        if (classConstantID !== '') {

            //find the class constant with the id constant
            classConstant = HPEConstants.componentclass[classConstantID];

            //do nothing unless constant is known to be valid
            if (this.model.validTabConstants.indexOf(HPEConstants.componentclass[classConstantID]) !== -1) {

                //do nothing unless component is known to be valid
                if (this.availableComponents[classConstant]) {
                    //update tab state/view with selected component
                    this.updateTabState(classConstantID);
                } else {
                    // Warn Message
                    HPELog.debug('[Products]: dynamic tab container invalid component: ', classConstantID);
                }
            }
        }
    },

    /**
     * handle child init event, triggered on initialization of child elements
     * only one AEM rendered child should trigger event, when this is received dynamic tab container
     * gets a reference to the newly initted controller object --
     * need to receive this since this is the one instance not instantiated by this controller
     * @param {object} child reference to controller object for rendered component
     */
    onDynamicTabContainerChildInit: function(data) {

        var classConstant;

        //only subscribe to the first broadcast of this event
        if (! this.isInited) {

            this.isInited = true;

            //workaround for double invocation on non-ampersand object
            if (this instanceof AmpersandState) {

                //get class constant reference from DOM
                classConstant = this.getDefaultChildClassConstantFromDOM();

                if (classConstant) {

                    //save reference to controller instance
                    this.model.pool[classConstant] = data.child;

                    //update model with currently active tab/panel component
                    this.model.currentDynamicTabConstant = classConstant;

                } else {
                    // Warn Message
                    HPELog.debug('[Products]: dynamic tab container invalid default component: ', classConstant);
                }
            }
        }
    }
});
